import { ChangeEvent, FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { navigateToGeneratedDocuments, navigateToSearchDocuments } from '../../../../utils/navigator'
import Doc from "../../../../models/document";
import Employee from "../../../../models/employee";
import TextInput from "../../../../Components/molecule/inputs/text-input";
import DateInput from "../../../../Components/molecule/inputs/date-input";
import CheckboxInput from "../../../../Components/molecule/inputs/checkbox-input";
import TextAreaInput from "../../../../Components/molecule/inputs/textarea-inpuit";
import SelectInput from "../../../../Components/molecule/inputs/select-input";
import { useDispatch, useSelector } from "react-redux";
import { HumanResourcesSliceState } from "slices/human-resources/reducer";
import { createSelector } from "reselect"
import { getFields, getManagers, viewDocument, searchRecruits } from "slices/human-resources/thunk";
import withRouter from "../../../../Components/Common/withRouter";
import { toast } from "react-toastify";
import GenerateDocumentsTemplate from "Components/templates/hr/document-generator/generate-documents-template";
import { JobTitle, OfficeLocation } from "models/org-chart-node";
import { DocumentGeneratorState, finishProcessesingDocument, setDocumentFields, setDocumentTemplateValue, startProcessesingDocument, startViewingDocument, finishViewingDocument, setGenerationError, upsertGeneratedDoc } from "slices/human-resources/document-generator/reducer"
import { getDocument, setDocumentValue, saveDocumentAsDraft, generateValidationSchema, submitDocument, getGeneratedDocument, generateDocument, approveDocument, rejectDocument, rejectDocumentByCandidate, approveOnBehalf as approveOnBehalfFunctioon } from "slices/human-resources/document-generator/thunk";
import { FormikHelpers, useFormik } from "formik";
import Field from "../../../../models/field";
import { GEN_DOC_STATUS } from "models/gen-doc-status";
import GeneratedDoc from "models/generated-doc";
import useManual from "hooks/useManual"
import useGeneratedDocPermissions from "hooks/useGeneratedDocPermissions";
import { PermissionsSliceState } from "slices/auth/permissions/reducer";
import { ProfileSliceState } from "slices/auth/profile/reducer";
import RejectDocumentFormFields from "Components/organisms/forms/reject-document-form/types";
import { RejectDocumentFormValidation } from "data/reject-document-form";
import { SCREENS } from "models/screen";
import DocumentGeneratorUtility from "utils/models/document-generator-utility";
import RecruitForm from "Components/organisms/forms/recruit-form";
import RecruitFormFields from "Components/organisms/forms/recruit-form/types";
import { RecruitFormValidation } from "data/recruit-form";
import Recruit from "models/recruit";



const KPI = [
    { value: '', label: 'Select KPI' },
    { value: '5', label: '5% (Employee)' },
    { value: '10', label: '10% (Lead)' },
    { value: '15', label: '15% (Manager)' },
    { value: '20', label: '20% (Director)' },
]

const selectHumanResources = createSelector(
    (state: any) => state.HumanResources,
    ({ jobTitles, officeLocations, managers }: HumanResourcesSliceState) => ({
        jobTitles, officeLocations, managers
    })
)


const selectDocumentGenerator = createSelector(
    (state: any) => state.DocumentGenerator,
    ({
        doc,
        savedValues,
        generateDocError,
        processingDocument,
        viewingDocument,
        docFields
    }: DocumentGeneratorState) => ({
        doc, savedValues, generateDocError, processingDocument, viewingDocument, docFields
    })
)


const selectPermissions = createSelector(
    (state: any) => state.Permissions,
    ({ screens }: PermissionsSliceState) => ({
        screens
    })
)

const selectProfile = createSelector(
    (state: any) => state.Profile,
    ({ employee }: ProfileSliceState) => ({
        employee
    })
)


const GenerateDocumentPage = (props: any) => {
    document.title = "Generate Document | IbexIQ";
    const { handleOpenManual } = useManual("human-resources/document-generator");
    const dispatch: any = useDispatch();
    const [searchParams] = useSearchParams();
    const [genDocStatus, setGenDocStatus] = useState<GEN_DOC_STATUS | null>(null);
    const [generatedDoc, setGeneratedDoc] = useState<GeneratedDoc | null>(null);
    const [showRejectModal, setShowRejectModal] = useState<boolean>(false);
    const [showCandidateRejectModal, setShowCandidateRejectModal] = useState<boolean>(false);
    const [showApproveModal, setShowApproveModal] = useState<boolean>(false);
    const [showApproveOnBehalfModal, setShowApproveOnBehalfModal] = useState<boolean>(false);
    const [recruitSearchQuery, setRecruitSearchQuery] = useState<string>("");
    const [recruitSearchResults, setRecruitSearchResults] = useState<Recruit[]>([]);
    const [searchingRecruits, setSearchingRecruits] = useState<boolean>(false);
    const intentRef = useRef<"draft" | 'submit'>("draft")

    const { doc, savedValues, generateDocError, processingDocument, viewingDocument, docFields } = useSelector(selectDocumentGenerator)
    const { jobTitles, officeLocations, managers } = useSelector(selectHumanResources)
    const { employee } = useSelector(selectProfile);
    const { screens } = useSelector(selectPermissions);
    const initialValues = useMemo(() => {
        const res = {};
        docFields.forEach((v: Field) => res[v.name] = savedValues[v.name] ?? "")
        return res;
    }, [savedValues, docFields])

    const validationSchema = useMemo(() => generateValidationSchema(docFields), [docFields])

    const recruitJobTitleOptions = useMemo(() => {
        return [
            { value: '', label: 'Select Position' },
            ...jobTitles.map((jobTitle: JobTitle) => ({ value: jobTitle.id, label: jobTitle.description }))
        ];
    }, [jobTitles])

    const recruitManagerOptions = useMemo(() =>
        DocumentGeneratorUtility.employeeOptions(managers, undefined, 'Select Hiring Manager')
        , [managers])

    const handleSearchRecruits = useCallback(async () => {
        if (!recruitSearchQuery.trim()) return;
        setSearchingRecruits(true);
        const results = await searchRecruits(recruitSearchQuery.trim());
        setRecruitSearchResults(results);
        setSearchingRecruits(false);
    }, [recruitSearchQuery])

    const {
        isHRAdmin,
        submittable,
        draftable,
        downloadable,
        editable,
        approvable,
        rejectable,
        candidateRejectable,
        approveOnBehalf,
        approvalBlockedReason
    } = useGeneratedDocPermissions(generatedDoc, genDocStatus)

    const approvers: Employee[] = useMemo(() => {
        if (!generatedDoc) return [];
        const res: Employee[] = []
        if (generatedDoc.managerAppr && !generatedDoc.managerApprovedAt)
            res.push(generatedDoc.managerAppr);

        if (generatedDoc.higherMgrAppr && !generatedDoc.higherManagerApprovedAt)
            res.push(generatedDoc.higherMgrAppr);

        return res
    }, [generatedDoc])

    const initializePage = async (documentId: string, generatedDocId: string | null) => {
        const [doc, formFields, _blank, generatedDoc] = await Promise.all([
            dispatch(getDocument(documentId)),
            dispatch(getFields()),
            dispatch(getManagers()),
            generatedDocId ? getGeneratedDocument(generatedDocId) : undefined
        ])


        if (!doc) {
            toast.error("Something went wrong. Could not find required document files")
            props.router.navigate(-1)
            return
        }

        if (!formFields) {
            toast.error("Something went wrong. Could not find document fields")
            props.router.navigate(-1)
            return
        }

        // If generated doc is found set saved values;
        if (generatedDoc) {
            setGeneratedDoc(generatedDoc)
            if (!generatedDoc.fields) {
                toast.error("Something went wrong. Could not autofill fields.")
                return;
            };

            const savedValues = {};
            const fields = generatedDoc.fields;
            for (let i = 0; i < fields.length; i++) {
                const f = fields[i].field
                if (f) {
                    savedValues[f.name] = fields[i].value;
                }
            }

            if (generatedDoc.status)
                setGenDocStatus(generatedDoc.status?.description as GEN_DOC_STATUS)

            dispatch(setDocumentTemplateValue(savedValues));

            const genDocRecruit = generatedDoc.genDocRecruits?.[0];
            if (genDocRecruit) {

                recruitFormik.setValues({
                    firstName: genDocRecruit.firstName,
                    middleName: genDocRecruit.middleName ?? '',
                    lastName: genDocRecruit.lastName,
                    commonName: genDocRecruit.commonName ?? '',
                    email: genDocRecruit.email,
                    phone: genDocRecruit.phone ?? '',
                    address: genDocRecruit.address ?? '',
                    jobTitleId: formFields.jobTitles.find(j => j.description === genDocRecruit.jobTitle)?.id ?? '',
                    managerId: generatedDoc.managerApproverId ?? ''
                });

            }
        }
    }

    const handleBack = useCallback(() => {
        setDocumentFields([])
        props.router.navigate(-1)
    }, [props.router.navgate])

    useEffect(() => {
        const docId = searchParams.get('docId');
        const generatedDocId = searchParams.get('genDocId');

        if (docId) {
            initializePage(docId, generatedDocId);
        } else {
            toast.error("Something went wrong. Document Id not found")
            navigateToSearchDocuments(props.router.navigate)
        }

        return () => dispatch(setDocumentTemplateValue({}))
    }, [searchParams, props])

    const documentGeneratorFormik = useFormik({
        initialValues,
        onSubmit: () => { },
        validationSchema,
        enableReinitialize: true
    })

    const recruitFormik = useFormik<RecruitFormFields>({
        initialValues: {
            firstName: '', middleName: '', lastName: '', commonName: '',
            email: '', phone: '', address: '', jobTitleId: '', managerId: ''
        },
        validationSchema: RecruitFormValidation,
        onSubmit: () => { }
    })

    const performSubmit = useCallback(async () => {

        dispatch(startProcessesingDocument())
        if (!doc) return;
        const generatedDocId = searchParams.get('genDocId');

        let res: GeneratedDoc | null = null;
        if (intentRef.current === "draft") {
            res = await dispatch(saveDocumentAsDraft(doc, recruitFormik.values, documentGeneratorFormik.values, generatedDocId));
        } else {
            res = await dispatch(submitDocument(doc, recruitFormik.values, documentGeneratorFormik.values, generatedDocId));
        }

        // Success
        if (res) {
            props.router.navigate(-1)
            dispatch(upsertGeneratedDoc({ ...generatedDoc, document: doc, ...res }));
        }

        dispatch(finishProcessesingDocument(""))
    }, [doc, recruitFormik.values, documentGeneratorFormik.values, searchParams, generatedDoc])

    const handleSelectRecruit = useCallback((recruit: Recruit) => {
        recruitFormik.setValues({
            firstName: recruit.firstName,
            middleName: recruit.middleName ?? '',
            lastName: recruit.lastName,
            commonName: recruit.commonName ?? '',
            email: recruit.email,
            phone: recruit.phone ?? '',
            address: recruit.address ?? '',
            jobTitleId: recruit.jobTitleId,
            managerId: recruit.managerId ?? ''
        });
        setRecruitSearchResults([]);
    }, [recruitFormik.setValues])

    const handleRejectDocument = useCallback(async (values: RejectDocumentFormFields, helpers: FormikHelpers<RejectDocumentFormFields>) => {

        if (!generatedDoc) return;
        const res = await rejectDocument(values.reason, generatedDoc?.id)

        // If approved, navigate to previous page.
        if (res) {
            handleBack();
            dispatch(upsertGeneratedDoc({ ...generateDocument, ...res }))
        }

    }, [generatedDoc, handleBack])

    const rejectDocumentFormik = useFormik({
        initialValues: {
            reason: ''
        } as RejectDocumentFormFields,
        validationSchema: RejectDocumentFormValidation,
        onSubmit: handleRejectDocument
    })

    const handleRejectDocumentByCandidate = useCallback(async (values: RejectDocumentFormFields, helpers: FormikHelpers<RejectDocumentFormFields>) => {

        if (!generatedDoc) return;
        const res = await rejectDocumentByCandidate(values.reason, generatedDoc.id)

        if (res) {
            handleBack();
            dispatch(upsertGeneratedDoc({ ...generateDocument, ...res }))
        }

    }, [generatedDoc, handleBack])

    const candidateRejectDocumentFormik = useFormik({
        initialValues: {
            reason: ''
        } as RejectDocumentFormFields,
        validationSchema: RejectDocumentFormValidation,
        onSubmit: handleRejectDocumentByCandidate
    })



    const handleChangeTemplateValues = useCallback((field: string, value: any) => {
        documentGeneratorFormik.handleChange(field)(value)
    }, [documentGeneratorFormik.handleChange])

    const handleViewDocument = useCallback(async (document: Doc) => {
        dispatch(startViewingDocument());
        const success = await viewDocument(document.id, generatedDoc?.id);
        if (!success) {
            dispatch(setGenerationError("Could not load document preview."));
        }
        dispatch(finishViewingDocument());
    }, [generatedDoc]);

    const renderInput = useCallback((field: Field) => {
        let isRequired = true;

        if (field.docFields && field.docFields.length > 0)
            isRequired = field.docFields[0].isRequired;

        const colClassName = field.type === 'text-area' ? "col-12" : "col-12 col-md-6 col-lg-4";

        if (field.type === 'string') {
            return <TextInput
                className={colClassName}
                disabled={!editable}
                message={documentGeneratorFormik.touched[field.name] ? documentGeneratorFormik.errors[field.name] : undefined}
                onBlur={documentGeneratorFormik.handleBlur}
                required={isRequired}
                name={field.name}
                error={!!documentGeneratorFormik.touched[field.name] && !!documentGeneratorFormik.errors[field.name]}
                key={field.name}
                value={documentGeneratorFormik.values[field.name]}
                label={field.label}
                placeholder={field.label}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleChangeTemplateValues(field.name, e.target.value)}
            />
        } else if (field.type === 'number') {
            return <TextInput
                className={colClassName}
                disabled={!editable}
                message={documentGeneratorFormik.touched[field.name] ? documentGeneratorFormik.errors[field.name] : undefined}
                onBlur={documentGeneratorFormik.handleBlur}
                name={field.name}
                error={!!documentGeneratorFormik.touched[field.name] && !!documentGeneratorFormik.errors[field.name]}
                step={"00.01"}
                required={isRequired}
                value={documentGeneratorFormik.values[field.name]}
                key={field.name}
                label={field.label}
                placeholder={field.label}
                type='number'
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleChangeTemplateValues(field.name, e.target.value)}
            />
        } else if (field.type === 'date') {
            return <DateInput
                className={colClassName}
                disabled={!editable}
                message={documentGeneratorFormik.touched[field.name] ? documentGeneratorFormik.errors[field.name] : undefined}
                onBlur={documentGeneratorFormik.handleBlur}
                name={field.name}
                error={!!documentGeneratorFormik.touched[field.name] && !!documentGeneratorFormik.errors[field.name]}
                required={isRequired}
                value={documentGeneratorFormik.values[field.name]}
                key={field.name}
                label={field.label}
                placeholder={field.label}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    handleChangeTemplateValues(field.name, e.target.value)
                }}
            />
        } else if (field.type === 'boolean') {
            return <CheckboxInput
                className={colClassName}
                disabled={!editable}
                message={documentGeneratorFormik.touched[field.name] ? documentGeneratorFormik.errors[field.name] : undefined}
                onBlur={documentGeneratorFormik.handleBlur}
                name={field.name}
                error={!!documentGeneratorFormik.touched[field.name] && !!documentGeneratorFormik.errors[field.name]}
                required={isRequired}
                value={documentGeneratorFormik.values[field.name]}
                key={field.name}
                label={field.label}
                type='number'
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    handleChangeTemplateValues(field.name, e.target.checked)
                }}
            />
        } else if (field.type === 'text-area') {
            return <TextAreaInput
                disabled={!editable}
                message={documentGeneratorFormik.touched[field.name] ? documentGeneratorFormik.errors[field.name] : undefined}
                onBlur={documentGeneratorFormik.handleBlur}
                name={field.name}
                error={!!documentGeneratorFormik.touched[field.name] && !!documentGeneratorFormik.errors[field.name]}
                required={isRequired}
                className={colClassName}
                value={documentGeneratorFormik.values[field.name]}
                key={field.name}
                label={field.label}
                placeholder={field.label}
                max={3000}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => handleChangeTemplateValues(field.name, e.target.value)}
            />
        } else if (field.type === 'select') {
            if (field.name === 'officeAddy') {
                return <SelectInput
                    className={colClassName}
                    disabled={!editable}
                    message={documentGeneratorFormik.touched[field.name] ? documentGeneratorFormik.errors[field.name] : undefined}
                    onBlur={documentGeneratorFormik.handleBlur}
                    name={field.name}
                    error={!!documentGeneratorFormik.touched[field.name] && !!documentGeneratorFormik.errors[field.name]}
                    required={isRequired}
                    key={field.name}
                    value={documentGeneratorFormik.values[field.name]}
                    label={field.label}
                    options={[{ value: '', label: 'Select Office' }, ...officeLocations.map((office: OfficeLocation) => ({ value: office.description, label: office.description }))]}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => handleChangeTemplateValues(field.name, e.target.value)}
                />
            } else if (field.name === "kpi") {
                return <SelectInput
                    className={colClassName}
                    disabled={!editable}
                    message={documentGeneratorFormik.touched[field.name] ? documentGeneratorFormik.errors[field.name] : undefined}
                    onBlur={documentGeneratorFormik.handleBlur}
                    name={field.name}
                    error={!!documentGeneratorFormik.touched[field.name] && !!documentGeneratorFormik.errors[field.name]}
                    required={isRequired}
                    key={field.name}
                    value={documentGeneratorFormik.values[field.name]}
                    label={field.label}
                    options={KPI}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => handleChangeTemplateValues(field.name, e.target.value)}
                />
            } else if (field.name === 'reportTo') {
                return <SelectInput
                    className={colClassName}
                    disabled={!editable}
                    message={documentGeneratorFormik.touched[field.name] ? documentGeneratorFormik.errors[field.name] : undefined}
                    onBlur={documentGeneratorFormik.handleBlur}
                    name={field.name}
                    error={!!documentGeneratorFormik.touched[field.name] && !!documentGeneratorFormik.errors[field.name]}
                    required={isRequired}
                    key={field.name}
                    value={documentGeneratorFormik.values[field.name]}
                    label={field.label}
                    options={[{ value: '', label: 'Select Report To' }, ...jobTitles.map((jobTitle: JobTitle) => ({ value: jobTitle.description, label: jobTitle.description }))]}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => handleChangeTemplateValues(field.name, e.target.value)}
                />
            }
        }
    }, [documentGeneratorFormik.values, documentGeneratorFormik.handleBlur, documentGeneratorFormik.errors, documentGeneratorFormik.touched, draftable, managers, generatedDoc, jobTitles, officeLocations])

    const handleSubmitWithIntent = useCallback(async (intent: 'draft' | 'submit') => {
        intentRef.current = intent;

        // Recalling a Pending doc back to Draft is a pure status change -- nothing is being
        // edited, so there's nothing to validate.
        if (intent === 'draft' && genDocStatus === GEN_DOC_STATUS.PENDING) {
            await performSubmit();
            return;
        }

        await recruitFormik.submitForm();
        const recruitErrors = await recruitFormik.validateForm();
        await documentGeneratorFormik.submitForm();
        const docErrors = await documentGeneratorFormik.validateForm();

        if (Object.keys(recruitErrors).length > 0 || Object.keys(docErrors).length > 0) return;

        await performSubmit();
    }, [genDocStatus, recruitFormik.submitForm, recruitFormik.validateForm, documentGeneratorFormik.submitForm, documentGeneratorFormik.validateForm, performSubmit]);

    const handleSubmitWithIntentDraft = useCallback(() => {
        handleSubmitWithIntent('draft');
    }, [handleSubmitWithIntent]);

    const handleSubmitWithIntentSubmit = useCallback(() => {
        handleSubmitWithIntent('submit');
    }, [handleSubmitWithIntent]);

    const handleShowApproveModal = useCallback(() => {
        setShowApproveModal(true)
    }, [setShowApproveModal])

    const handleHideApprovefModal = useCallback(() => {
        setShowApproveModal(false)
    }, [setShowApproveModal])

    const handleApproveDocument = useCallback(async () => {
        if (!generatedDoc) return;

        dispatch(startProcessesingDocument())
        setShowApproveModal(false)
        try {
            const res = await approveDocument(generatedDoc.id);
            // If approved, navigate to previous page.
            if (res) {
                handleBack();
                dispatch(upsertGeneratedDoc({ ...generateDocument, document: doc ?? undefined, ...res }))
            }
            dispatch(finishProcessesingDocument(""))
        } catch (err: any) {
            dispatch(finishProcessesingDocument(err.message))
        }
    }, [generatedDoc, handleBack])


    const handleDownloadDocument = useCallback(async () => {
        const genDocId = searchParams.get('genDocId');

        if (!genDocId)
            return;

        await dispatch(generateDocument(genDocId));
    }, [searchParams])

    const handleShowRejectModal = useCallback(() => {
        setShowRejectModal(true)
    }, [setShowRejectModal])

    const handleHideRejectModal = useCallback(() => {
        setShowRejectModal(false)
        rejectDocumentFormik.handleReset({})
    }, [setShowRejectModal, rejectDocumentFormik.handleReset])

    const handleShowCandidateRejectModal = useCallback(() => {
        setShowCandidateRejectModal(true)
    }, [setShowCandidateRejectModal])

    const handleHideCandidateRejectModal = useCallback(() => {
        setShowCandidateRejectModal(false)
        candidateRejectDocumentFormik.handleReset({})
    }, [setShowCandidateRejectModal, candidateRejectDocumentFormik.handleReset])

    const handleShowApproveOnBehalfModal = useCallback(() => {
        setShowApproveOnBehalfModal(true)
    }, [setShowApproveOnBehalfModal])

    const handleHideApproveOnBehalfModal = useCallback(() => {
        setShowApproveOnBehalfModal(false)
        rejectDocumentFormik.handleReset({})
    }, [setShowApproveOnBehalfModal, rejectDocumentFormik.handleReset])

    const handleApproveOnBehalf = async (employees: string[]) => {
        if (!generatedDoc) return;
        dispatch(startProcessesingDocument());
        try {
            const res = await approveOnBehalfFunctioon(generatedDoc.id, employees);
            if (res) {
                dispatch(upsertGeneratedDoc({ ...generatedDoc, document: doc ?? undefined, ...res }));
                handleBack();
            }
            dispatch(finishProcessesingDocument(""));
        } catch (err: any) {
            dispatch(finishProcessesingDocument(err.message));
        }
    }

    return <GenerateDocumentsTemplate
        fields={docFields}
        docStatus={genDocStatus ?? undefined}
        doc={doc ?? undefined}
        processingDocument={processingDocument}
        viewingDocument={viewingDocument}
        generateDocError={generateDocError}
        renderInput={renderInput}
        recruitFormSection={<RecruitForm
            values={recruitFormik.values}
            errors={recruitFormik.errors}
            touched={recruitFormik.touched}
            handleChange={recruitFormik.handleChange}
            handleBlur={recruitFormik.handleBlur}
            handleSubmit={recruitFormik.handleSubmit}
            isValid={recruitFormik.isValid}
            isSubmitting={recruitFormik.isSubmitting}
            jobTitles={recruitJobTitleOptions}
            managers={recruitManagerOptions}
            searchQuery={recruitSearchQuery}
            onSearchQueryChange={setRecruitSearchQuery}
            onSearch={handleSearchRecruits}
            searching={searchingRecruits}
            searchResults={recruitSearchResults}
            onSelectRecruit={handleSelectRecruit}
            editable={editable}
        />}
        handleViewDocument={handleViewDocument}
        handleDownloadDocument={downloadable ? handleDownloadDocument : undefined}
        handleSubmitDocument={submittable ? handleSubmitWithIntentSubmit : undefined}
        handleDrafDocument={draftable ? handleSubmitWithIntentDraft : undefined}
        handleApproveDocument={approvable ? handleApproveDocument : undefined}
        handleShowRejectModal={rejectable ? handleShowRejectModal : undefined}
        handleHideRejectModal={rejectable ? handleHideRejectModal : undefined}
        handleShowCandidateRejectModal={candidateRejectable ? handleShowCandidateRejectModal : undefined}
        handleHideCandidateRejectModal={candidateRejectable ? handleHideCandidateRejectModal : undefined}
        showCandidateRejectModal={showCandidateRejectModal}
        candidateRejectForm={{
            values: candidateRejectDocumentFormik.values,
            errors: candidateRejectDocumentFormik.errors,
            touched: candidateRejectDocumentFormik.touched,
            handleChange: candidateRejectDocumentFormik.handleChange,
            handleBlur: candidateRejectDocumentFormik.handleBlur,
            handleSubmit: candidateRejectDocumentFormik.handleSubmit,
            isValid: candidateRejectDocumentFormik.isValid,
            isSubmitting: candidateRejectDocumentFormik.isSubmitting,
        }}
        handleApproveOnBehalf={approveOnBehalf ? handleApproveOnBehalf : undefined}
        handleHideApproveOnBehalf={handleHideApproveOnBehalfModal}
        handleShowApproveOnBehalf={approveOnBehalf ? handleShowApproveOnBehalfModal : undefined}
        handleHideApproveModal={approvable ? handleHideApprovefModal : undefined}
        handleShowApproveModal={approvable ? handleShowApproveModal : undefined}
        handleBack={handleBack}
        handleOpenHelpDocument={handleOpenManual}
        showApproveOnBehalfModal={showApproveOnBehalfModal}
        showRejectModal={showRejectModal}
        showApproveModal={showApproveModal}
        values={rejectDocumentFormik.values}
        errors={rejectDocumentFormik.errors}
        touched={rejectDocumentFormik.touched}
        handleChange={rejectDocumentFormik.handleChange}
        handleBlur={rejectDocumentFormik.handleBlur}
        handleSubmit={rejectDocumentFormik.handleSubmit}
        isValid={rejectDocumentFormik.isValid}
        isSubmitting={rejectDocumentFormik.isSubmitting}
        approvers={approvers}
        approvalBlockedReason={approvalBlockedReason}
    />
}

export default withRouter(GenerateDocumentPage);